import type { ReactNode } from 'react'

interface EmptyStateProps {
  /** Material Symbols icon name (e.g. "inbox", "search_off"). */
  icon?: string
  /** Main headline. */
  title: string
  /** Secondary explanatory text. */
  description?: ReactNode
  /** Right-aligned primary action (button / link). */
  action?: ReactNode
  /** `surface` (default) for flat content pages; `glass` for empty states rendered on glass surfaces. */
  variant?: 'surface' | 'glass'
  /** `lg` for pages with a larger minimum text size (/schule, 18 px body). */
  size?: 'md' | 'lg'
}

/**
 * Canonical empty-state block. Use when a list/table has zero rows or
 * a query returned nothing. Chrome-tone palette, quiet, no gradient.
 * DESIGN.md §8b minimalism — empty states are helpful, not celebratory.
 */
export function EmptyState({ icon = 'inbox', title, description, action, variant = 'surface', size = 'md' }: EmptyStateProps) {
  const shellCls = variant === 'glass'
    ? 'liquid-glass rounded-3xl'
    : 'bg-surface-container-lowest border border-outline-variant'
  return (
    <div className={`${shellCls} px-6 py-10 text-center`}>
      <span
        className="material-symbols-outlined text-on-surface-variant mb-3"
        style={{ fontSize: 44 }}
        aria-hidden="true"
      >
        {icon}
      </span>
      <h3 className={`font-headline font-bold text-on-surface ${size === 'lg' ? 'text-2xl' : 'text-base'}`}>{title}</h3>
      {description && (
        <p className={`${size === 'lg' ? 'text-lg' : 'text-sm'} text-on-surface-variant mt-1.5 max-w-md mx-auto`}>{description}</p>
      )}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  )
}
